import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  addClub,
  getAllClubs,
  updateClub,
  deleteClub,
  searchClubs,
  getAllUsers,
  updateClubStatusAction,
} from "actions/action";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@mui/material";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import ReactDOMServer from "react-dom/server";

const Clubs = () => {
  const [clubs, setClubs] = useState([]);
  const [clubName, setClubName] = useState("");
  const [clubLink, setClubLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editLink, setEditLink] = useState("");
  const [query, setQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searching, setSearching] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedClubUsers, setSelectedClubUsers] = useState([]);
  const [currentClubName, setCurrentClubName] = useState("");
  const [userLoading, setUserLoading] = useState(false);

  const fetchClubs = async () => {
    setLoading(true);
    setError("");
    const res = await getAllClubs();
    if (res && res.success) {
      setClubs(res.data || []);
    } else if (res && res.err) {
      setError("Failed to load clubs");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchClubs();
  }, []);

  // Debounced search for clubs
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length === 0) return;
    setSearching(true);
    const id = setTimeout(async () => {
      const res = await searchClubs(trimmed);
      if (res && res.success) {
        setClubs(res.data || []);
      } else if (res && res.err) {
        // keep previous list
      } else if (res && res.success === false) {
        // API returns 404 with success false
        setClubs([]);
      }
      setSearching(false);
    }, 400);
    return () => clearTimeout(id);
  }, [query]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!clubName.trim()) return;
    setLoading(true);
    setError("");
    const res = await addClub({ club_name: clubName.trim(), club_link: clubLink.trim() });
    if (res && res.success) {
      setClubName("");
      setClubLink("");
      setShowCreateModal(false);
      fetchClubs();
    } else if (res && res.err) {
      setError("Failed to create club");
      setLoading(false);
    } else {
      setClubName("");
      setClubLink("");
      setShowCreateModal(false);
      fetchClubs();
    }
  };

  const startEdit = (club) => {
    setEditingId(club.club_id);
    setEditName(club.club_name);
    setEditLink(club.club_link || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditLink("");
  };

  const saveEdit = async (id) => {
    if (!editName.trim()) return;
    setLoading(true);
    setError("");
    const res = await updateClub(id, { club_name: editName.trim(), club_link: editLink.trim() });
    if (res && res.err) {
      setError("Failed to update club");
      setLoading(false);
      return;
    }
    cancelEdit();
    fetchClubs();
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to delete these records?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: ReactDOMServer.renderToString(
        <>
          Yes, delete it!
          <FontAwesomeIcon icon={faTrash} style={{ marginLeft: "10px" }} />
        </>
      ),
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        setError("");
        deleteClub(id).then((res) => {
          if (res && res.err) {
            setError("Failed to delete club");
            setLoading(false);
            return;
          }
          fetchClubs();
          Swal.fire({
            title: "Deleted!",
            text: "Club record deleted successfully.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
        }).catch(() => {
          setError("Failed to delete club");
          setLoading(false);
        });
      }
    });
  };

  const handleViewUsers = async (clubInfo) => {
    setCurrentClubName(clubInfo.club_name);
    setShowUserModal(true);
    setUserLoading(true);
    const res = await getAllUsers();
    if (res && (res.status === 200 || res.success)) {
      // Filter users for this club
      const allUsers = res.data.data || res.data || [];
      const filtered = allUsers.filter(u => u.club_id === clubInfo.club_id);
      setSelectedClubUsers(filtered);
    } else {
      Swal.fire("Error", "Failed to fetch users", "error");
    }
    setUserLoading(false);
  };

  const handleUpdateStatus = async (userId, status) => {
    const res = await updateClubStatusAction(userId, status);
    if (res && res.success) {
      Swal.fire("Success", `User ${status} successfully`, "success");
      // refresh local list
      setSelectedClubUsers(prev => prev.map(u => u.user_id === userId ? { ...u, club_status: status } : u));
    } else {
      Swal.fire("Error", "Failed to update status", "error");
    }
  };

  const UserModal = () => {
    if (!showUserModal) return null;
    return (
      <div
        className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none"
      >
        <div className="relative w-auto my-6 mx-auto max-w-3xl" style={{ width: '80%' }}>
          <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
            <div className="flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t">
              <h3 className="text-2xl font-semibold">
                Users in {currentClubName}
              </h3>
              <button
                className="p-1 ml-auto bg-transparent border-0 text-black opacity-5 float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                onClick={() => setShowUserModal(false)}
              >
                <span className="bg-transparent text-black opacity-5 h-6 w-6 text-2xl block outline-none focus:outline-none">
                  ×
                </span>
              </button>
            </div>
            <div className="relative p-6 flex-auto max-h-[60vh] overflow-y-auto">
              <table className="items-center w-full bg-transparent border-collapse text-left">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-blueGray-500 text-xs font-semibold">Name</th>
                    <th className="px-4 py-2 text-blueGray-500 text-xs font-semibold">Email</th>
                    <th className="px-4 py-2 text-blueGray-500 text-xs font-semibold">Status</th>
                    <th className="px-4 py-2 text-blueGray-500 text-xs font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {userLoading ? (
                    <tr><td colSpan="4" className="text-center">Loading...</td></tr>
                  ) : selectedClubUsers.length === 0 ? (
                    <tr><td colSpan="4" className="text-center">No users found in this club</td></tr>
                  ) : (
                    selectedClubUsers.map(user => (
                      <tr key={user.user_id} className="border-t">
                        <td className="px-4 py-2 text-sm">{user.first_name} {user.last_name}</td>
                        <td className="px-4 py-2 text-sm">{user.email}</td>
                        <td className="px-4 py-2 text-sm">
                          <span className={`px-2 py-1 rounded text-xs text-white ${user.club_status === 'approved' ? 'bg-emerald-500' :
                            user.club_status === 'rejected' ? 'bg-red-500' :
                              'bg-orange-500'
                            }`}>
                            {user.club_status || 'Pending'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm flex gap-2">
                          <button
                            onClick={() => handleUpdateStatus(user.user_id, 'approved')}
                            className="bg-emerald-500 text-white text-xs px-2 py-1 rounded hover:bg-emerald-600"
                            disabled={user.club_status === 'approved'}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleUpdateStatus(user.user_id, 'rejected')}
                            className="bg-red-500 text-white text-xs px-2 py-1 rounded hover:bg-red-600"
                            disabled={user.club_status === 'rejected'}
                          >
                            Reject
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
              <button
                className="text-red-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                type="button"
                onClick={() => setShowUserModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const CreateClubModal = () => {
    if (!showCreateModal) return null;
    return (
      <div
        className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none bg-black bg-opacity-50"
      >
        <div className="relative w-auto my-6 mx-auto max-w-lg" style={{ width: '90%' }}>
          <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-blueGray-800 text-white outline-none focus:outline-none">
            <div className="flex items-start justify-between p-5 border-b border-solid border-blueGray-600 rounded-t">
              <h3 className="text-2xl font-semibold">Create New Club</h3>
              <button
                className="p-1 ml-auto bg-transparent border-0 text-white opacity-50 float-right text-3xl leading-none font-semibold outline-none focus:outline-none hover:opacity-100"
                onClick={() => setShowCreateModal(false)}
              >
                <span className="bg-transparent text-white h-6 w-6 text-2xl block outline-none focus:outline-none">
                  ×
                </span>
              </button>
            </div>
            <form onSubmit={onSubmit}>
              <div className="relative p-6 flex-auto">
                <div className="mb-4">
                  <label className="block text-white text-sm font-bold mb-2">
                    Club Name
                  </label>
                  <input
                    type="text"
                    value={clubName}
                    onChange={(e) => setClubName(e.target.value)}
                    required
                    className="border-0 px-3 py-3 placeholder-blueGray-400 text-white bg-blueGray-700 rounded text-sm shadow outline-none focus:outline-none focus:ring w-full"
                    placeholder="Enter club name"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-white text-sm font-bold mb-2">
                    Hyperlink (Optional)
                  </label>
                  <input
                    type="text"
                    value={clubLink}
                    onChange={(e) => setClubLink(e.target.value)}
                    className="border-0 px-3 py-3 placeholder-blueGray-400 text-white bg-blueGray-700 rounded text-sm shadow outline-none focus:outline-none focus:ring w-full"
                    placeholder="https://example.com"
                  />
                </div>
                {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
              </div>
              <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-600 rounded-b">
                <button
                  className="text-red-500 bg-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                >
                  Close
                </button>
                <button
                  className="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                  type="submit"
                  disabled={loading || !clubName.trim()}
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="relative">
        <div className="flex justify-between items-center px-4 mt-6">
          <p className="font-semibold text-lg text-white">Club Details</p>
        </div>

        <div className="px-6 mt-4 mb-6">
          <div className="flex flex-row flex-wrap items-center">
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-lightBlue-500 text-white active:bg-lightBlue-600 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150"
            >
              <i className="fas fa-plus mr-2"></i> Create Club
            </button>
          </div>
        </div>

        <div className="px-6 mt-4 mb-6">
          <form className="flex flex-row flex-wrap items-center">
            <div className="relative flex w-full flex-wrap items-stretch">
              <span className="z-10 h-full leading-snug font-normal absolute text-center text-white bg-transparent rounded text-base items-center justify-center w-8 pl-3 py-3">
                <i className="fas fa-search"></i>
              </span>
              <Tooltip title="Search Record" arrow>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search clubs by name"
                  className="border-0 px-3 py-3 placeholder-blueGray-400 text-white relative bg-blueGray-700 rounded text-sm shadow outline-none focus:outline-none focus:ring w-full pl-10 pr-10"
                />
              </Tooltip>
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-0 top-0 bottom-0 flex items-center justify-center w-10 text-white hover:text-blueGray-300 focus:outline-none"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
              {searching && (
                <div className="absolute right-12 top-1/2 -translate-y-1/2 text-xs text-blueGray-300">
                  Searching…
                </div>
              )}
            </div>
          </form>
        </div>

        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-blueGray-700">
          <div className="block w-full overflow-x-auto">
            <table className="items-center w-full bg-transparent border-collapse">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-xs uppercase whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                    S.No.
                  </th>
                  <th className="px-6 py-3 text-xs uppercase whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                    Name
                  </th>
                  <th className="px-6 py-3 text-xs uppercase whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && clubs.length === 0 ? (
                  <tr>
                    <td className="px-6 py-4 text-white" colSpan={3}>
                      Loading...
                    </td>
                  </tr>
                ) : clubs.length === 0 ? (
                  <tr>
                    <td className="px-6 py-4 text-center text-white" colSpan={3}>
                      No clubs found
                    </td>
                  </tr>
                ) : (
                  clubs.map((c, index) => (
                    <tr key={c.club_id} className="border-t border-blueGray-700">
                      <td className="px-6 py-4 text-white">{index + 1}</td>
                      <td className="px-6 py-4 text-white">
                        {editingId === c.club_id ? (
                          <div className="flex flex-col gap-2">
                            <input
                              className="border px-2 py-1 rounded text-sm bg-blueGray-800 text-white"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              placeholder="Club Name"
                            />
                            <input
                              className="border px-2 py-1 rounded text-sm bg-blueGray-800 text-white"
                              value={editLink}
                              onChange={(e) => setEditLink(e.target.value)}
                              placeholder="Hyperlink (Optional)"
                            />
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1">
                            <Link
                              to={`/student_club/${c.club_id}`}
                              className="text-lightBlue-400 hover:text-lightBlue-300 underline font-semibold transition-colors duration-200"
                            >
                              {c.club_name}
                            </Link>
                            {c.club_link && (
                              <a
                                href={c.club_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blueGray-400 hover:text-blueGray-300 transition-colors"
                              >
                                {c.club_link}
                              </a>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center flex gap-4">
                        {editingId === c.club_id ? (
                          <>
                            <button
                              onClick={() => saveEdit(c.club_id)}
                              className="bg-emerald-500 text-white text-xs font-bold uppercase px-3 py-2 rounded shadow hover:shadow-md mr-2"
                            >
                              Save
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="bg-blueGray-500 text-white text-xs font-bold uppercase px-3 py-2 rounded shadow hover:shadow-md"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleViewUsers(c)}
                              className="bg-lightBlue-500 text-white text-xs font-bold uppercase px-3 py-2 rounded shadow hover:shadow-md mx-2"
                            >
                              View Users
                            </button>
                            <Tooltip title="Edit" arrow>
                              <motion.button
                                whileHover={{ scale: 1.2 }}
                                whileTap={{ scale: 0.9 }}
                                type="button"
                                onClick={() => startEdit(c)}
                                className="focus:outline-none"
                                style={{ color: 'var(--accent-primary)' }}
                              >
                                <FontAwesomeIcon
                                  icon={faPenToSquare}
                                  className="text-xl"
                                />
                              </motion.button>
                            </Tooltip>
                            <Tooltip title="Delete" arrow>
                              <motion.button
                                whileHover={{ scale: 1.2 }}
                                whileTap={{ scale: 0.9 }}
                                type="button"
                                onClick={() => handleDelete(c.club_id)}
                                className="focus:outline-none mx-4"
                                style={{ color: 'var(--danger)' }}
                              >
                                <FontAwesomeIcon
                                  icon={faTrash}
                                  className="text-xl"
                                />
                              </motion.button>
                            </Tooltip>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div >
      <UserModal />
      <CreateClubModal />
    </>
  );
};

export default Clubs;
